import { Autocomplete, Box, TextField } from '@mui/material';
import { useFormikContext } from 'formik';
import React from 'react';

import { usePrograms } from 'hooks/useProgram';
import useTranslate from 'hooks/useTranslate';
import type { CaseProgramFormInterface, ProgramOption } from 'types';

interface ProgramFieldProps {
    userId?: string;
    active?: boolean;
    disabled?: boolean;
}

export const ProgramField: React.FC<ProgramFieldProps> = ({
    userId,
    active = true,
    disabled = false,
}) => {
    const { translate } = useTranslate();
    const { errors, setFieldTouched, setFieldValue, submitCount, touched, values } =
        useFormikContext<CaseProgramFormInterface>();

    const {
        data: programs = [],
        isFetching,
        isLoading,
    } = usePrograms(userId, values.country, {
        enabled: active,
    });

    const selectedProgram: ProgramOption | null =
        programs.find((option) => option.value === values.programName) ?? null;
    const programError = Boolean(errors.programName && (touched.programName || submitCount > 0));
    const waitingForCountry = !values.country;
    const programsBusy = isLoading || isFetching;

    return (
        <Autocomplete
            fullWidth
            size="small"
            disabled={disabled || waitingForCountry || (programsBusy && programs.length === 0)}
            loading={programsBusy}
            options={programs}
            value={selectedProgram}
            loadingText={translate('loadingServices')}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, current) => option.value === current.value}
            onChange={(_event, option) => {
                void setFieldValue('programName', option?.value ?? '', true);
            }}
            onBlur={() => {
                void setFieldTouched('programName', true);
            }}
            renderOption={(props, option) => {
                const { key, ...optionProps } = props;

                return (
                    <Box
                        component="li"
                        key={key}
                        {...optionProps}
                        sx={{
                            color: option.display_color || 'text.primary',
                        }}
                    >
                        <Box
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: option.display_color || 'text.disabled',
                                mr: 1,
                                flexShrink: 0,
                            }}
                        />
                        {option.label}
                    </Box>
                );
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    name="programName"
                    label={translate('service')}
                    error={programError}
                    helperText={
                        programError
                            ? errors.programName
                            : programsBusy && programs.length === 0
                              ? translate('loadingServices')
                              : ' '
                    }
                    sx={{
                        '& .MuiInputBase-input': {
                            color: selectedProgram?.display_color || 'inherit',
                        },
                    }}
                />
            )}
        />
    );
};
